import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, LineChart, Line, CartesianGrid, Legend
} from 'recharts';
import { TrendingUp, Download, Trash2, ChevronRight, Plus } from 'lucide-react';

const fmt  = (n) => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',minimumFractionDigits:0}).format(n||0);
const fmtH = (n) => new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR',minimumFractionDigits:2}).format(n||0);

export default function BusinessPlan() {
  const { generateBP, listBPs, listCapex, listOpex, deleteBP } = useApp();
  const { t } = useTranslation();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [capexList, setCapexList] = useState([]);
  const [opexList, setOpexList]   = useState([]);
  const [bpList, setBpList]       = useState([]);
  const [selectedCapex, setSelectedCapex] = useState(null);
  const [selectedOpex, setSelectedOpex]   = useState(null);
  const [hourlyRate, setHourlyRate]       = useState('');
  const [pricingMode, setPricingMode] = useState('target_margin'); // target_margin | manual
  const [targetMarginMode, setTargetMarginMode] = useState('daily'); // daily | hourly
  const [targetMarginValue, setTargetMarginValue] = useState('150');
  const [workDays, setWorkDays]           = useState(22);
  const [planName, setPlanName]           = useState('');
  const [apiError, setApiError] = useState('');
  const [bpData, setBpData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const scenarioLabel = (scenario) => {
    if (!scenario) return '—';
    let calcType = null;
    if (scenario.input_params && typeof scenario.input_params === 'object') {
      calcType = scenario.input_params.calculatorType;
      if (!calcType && Object.prototype.hasOwnProperty.call(scenario.input_params, 'payloadKg') && !Object.prototype.hasOwnProperty.call(scenario.input_params, 'totalKg')) {
        calcType = 'single_route';
      }
    } else if (typeof scenario.input_params === 'string') {
      try {
        const parsed = JSON.parse(scenario.input_params);
        calcType = parsed?.calculatorType || null;
        if (!calcType && Object.prototype.hasOwnProperty.call(parsed || {}, 'payloadKg') && !Object.prototype.hasOwnProperty.call(parsed || {}, 'totalKg')) {
          calcType = 'single_route';
        }
      } catch (_) {
        calcType = null;
      }
    }
    if (!calcType) {
      const n = String(scenario.name || '').toLowerCase();
      if (scenario.scenario_type === 'single_route' || n.includes('scenario sr') || n.startsWith('sr ') || n.includes(' single route')) {
        calcType = 'single_route';
      }
    }
    if (calcType === 'single_route') return '🛣️ Single Route';
    return scenario.scenario_type === 'middle_mile' ? '🚁 Middle Mile' : '☀️ Solar Panel';
  };

  useEffect(() => { loadLists(); }, []);

  useEffect(() => {
    const editId = location.state?.editId;
    if (editId && bpList.length > 0) loadBPById(editId);
  }, [location.state, bpList]);

  const loadLists = async () => {
    try {
      const [c, o, b] = await Promise.all([listCapex(), listOpex(), listBPs()]);
      setCapexList(c || []); setOpexList(o || []); setBpList(b || []);
    } catch(e) { console.error(e); }
  };

  const loadBPById = (id) => {
    const bp = bpList.find(b => b.id === id);
    if (!bp) return;
    setBpData({
      name: bp.name, totalCapex: bp.total_capex, monthlyOpex: bp.monthly_opex,
      monthlyRevenue: bp.monthly_revenue, monthlyProfit: bp.monthly_profit,
      breakevenMonth: bp.breakeven_month, roi60: bp.roi_60m,
      work_days_per_month: bp.work_days_per_month, service_hourly_rate: bp.service_hourly_rate,
      capexConfig: { drone_model: bp.drone_model, num_drones: bp.num_drones, leasing_monthly: bp.leasing_monthly },
      opexConfig: { scenario_type: bp.scenario_type, daily_cost: bp.daily_cost },
      projections: bp.projections || [],
    });
    setStep(3);
  };

  const generate = async () => {
    const isManual = pricingMode === 'manual';
    const linkedCapexId = selectedOpex?.config_id || selectedOpex?.configId || null;
    const capexId = linkedCapexId || selectedCapex?.id;
    if (!selectedOpex || !capexId || !planName || (isManual && !hourlyRate)) {
      alert(t('bp.fillAll')); return;
    }
    setApiError('');
    setLoading(true);
    try {
      const r = await generateBP({
        configId: capexId, scenarioId: selectedOpex.id, name: planName,
        serviceHourlyRate: parseFloat(hourlyRate),
        workDaysPerMonth: workDays,
        pricingMode,
        targetMarginMode,
        targetMarginValue: parseFloat(targetMarginValue) || 0
      });
      setBpData(r); await loadLists(); setStep(3);
    } catch(e) {
      const msg = e?.response?.data?.error || 'Errore nella generazione del Business Plan';
      setApiError(msg);
      console.error(e);
    }
    finally { setLoading(false); }
  };

  const getTargetPreview = () => {
    if (!effectiveCapex || !selectedOpex) return { hourly: 0, daily: 0 };
    const capex = parseFloat(effectiveCapex.total_capex) || 0;
    const dailyOpex = parseFloat(selectedOpex.daily_cost) || 0;
    const days = Math.max(1, parseInt(workDays || 22, 10));
    const margin = Math.max(0, parseFloat(targetMarginValue) || 0);
    const marginMonthly = targetMarginMode === 'hourly'
      ? margin * 8 * days
      : margin * days;
    const profitFromRoi = capex * 1.2 / 60; // allineato al backend (ROI minimo 20% in 60 mesi)
    const requiredProfit = Math.max(profitFromRoi, marginMonthly);
    const monthlyRevenue = (dailyOpex * days) + requiredProfit;
    const hourly = monthlyRevenue / (8 * days);
    return { hourly, daily: hourly * 8 };
  };

  const selectedOpexLinkedCapexId = selectedOpex?.config_id || selectedOpex?.configId || null;
  const effectiveCapex = selectedOpexLinkedCapexId
    ? capexList.find(c => String(c.id) === String(selectedOpexLinkedCapexId))
    : selectedCapex;
  const targetPreview = getTargetPreview();

  const handleDelete = async (id) => {
    if (!window.confirm(t('bp.confirmDelete'))) return;
    try { await deleteBP(id); await loadLists(); }
    catch(e) { alert(t('bp.deleteError')); }
  };

  const downloadPDF = () => {
    if (!bpData) return;
    const content = buildPDFContent(bpData);
    const blob = new Blob([content], { type:'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${bpData.name || 'BusinessPlan'}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  const buildPDFContent = (d) => {
    const rows = (d.projections || [])
      .filter((_,i) => (i+1) % 6 === 0)
      .map(p => `
        <tr style="border-bottom:1px solid #e5e7eb">
          <td style="padding:8px">${t('bp.pdf.month')} ${p.month}</td>
          <td style="padding:8px;text-align:right;color:#10b981">${fmt(p.monthlyRevenue)}</td>
          <td style="padding:8px;text-align:right;color:#0d9488">${fmt(p.monthlyOpex)}</td>
          <td style="padding:8px;text-align:right;font-weight:700">${fmt(p.monthlyProfit)}</td>
          <td style="padding:8px;text-align:right;font-weight:700;color:${p.cumulativeProfit>=0?'#10b981':'#ef4444'}">${fmt(p.cumulativeProfit)}</td>
        </tr>`).join('');

    return `<!DOCTYPE html>
<html lang="it"><head><meta charset="UTF-8"><title>Business Plan – ${d.name}</title>
<style>
  body{font-family:Arial,sans-serif;max-width:900px;margin:0 auto;padding:40px;color:#111}
  h1{color:#003a43;font-size:2rem;border-bottom:3px solid #003a43;padding-bottom:12px}
  h2{color:#003a43;font-size:1.2rem;margin-top:2rem}
  .grid{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:16px;margin:1.5rem 0}
  .box{background:#f8fafc;border-radius:8px;padding:16px}
  .val{font-size:1.4rem;font-weight:800;color:#003a43}
  .lbl{font-size:0.75rem;color:#6b7280;margin-bottom:4px}
  table{width:100%;border-collapse:collapse;margin-top:1rem}
  thead th{background:#003a43;color:white;padding:10px;text-align:left}
  .break{color:#0d9488;font-weight:700;font-size:1.1rem}
  @media print{button{display:none}}
</style></head>
<body>
  <h1>📊 Business Plan — ${d.name}</h1>
  <p style="color:#6b7280">${t('bp.pdf.generated')} ${new Date().toLocaleDateString('it-IT')} | ${t('bp.pdf.scenario60')}</p>
  <h2>1. ${t('bp.pdf.capexTitle')}</h2>
  <div class="grid">
    <div class="box"><div class="lbl">${t('bp.pdf.totalCapex')}</div><div class="val">${fmt(d.totalCapex)}</div></div>
    <div class="box"><div class="lbl">${t('bp.pdf.droneModel')}</div><div class="val">${d.capexConfig?.drone_model||'—'} ×${d.capexConfig?.num_drones||1}</div></div>
    
  </div>
  <h2>2. ${t('bp.pdf.opexTitle')}</h2>
  <div class="grid">
    <div class="box"><div class="lbl">${t('bp.pdf.scenarioType')}</div><div class="val">${d.opexConfig?.scenario_type==='middle_mile'?'Middle Mile':'Solar Panel'}</div></div>
    <div class="box"><div class="lbl">${t('bp.pdf.dailyCost')}</div><div class="val">${fmt(d.opexConfig?.daily_cost)}</div></div>
    <div class="box"><div class="lbl">${t('bp.pdf.monthlyOpex')}</div><div class="val">${fmt(d.monthlyOpex)}</div></div>
    <div class="box"><div class="lbl">${t('bp.pdf.workDays')}</div><div class="val">${d.work_days_per_month}</div></div>
  </div>
  <h2>3. ${t('bp.pdf.revenueTitle')}</h2>
  <div class="grid">
    <div class="box"><div class="lbl">${t('bp.pdf.hourlyRate')}</div><div class="val">${fmtH(d.service_hourly_rate)}/h</div></div>
    <div class="box"><div class="lbl">${t('bp.pdf.monthlyRevenue')}</div><div class="val">${fmt(d.monthlyRevenue)}</div></div>
    <div class="box"><div class="lbl">${t('bp.pdf.netProfit')}</div><div class="val">${fmt(d.monthlyProfit)}</div></div>
    <div class="box"><div class="lbl">${t('bp.pdf.roi60')}</div><div class="val" style="color:#10b981">${d.roi60}%</div></div>
  </div>
  <div style="margin:1.5rem 0;padding:1.25rem;background:#f0fdf4;border-radius:8px;border-left:4px solid #10b981">
    <span class="break">${t('bp.pdf.breakeven')}: ${d.breakevenMonth?`${t('bp.pdf.month')} ${d.breakevenMonth}`:t('bp.pdf.breakevenNA')}</span>
  </div>
  <h2>4. ${t('bp.pdf.tableTitle')}</h2>
  <table>
    <thead><tr><th>${t('bp.pdf.month')}</th><th style="text-align:right">${t('bp.pdf.revenue')}</th><th style="text-align:right">OPEX</th><th style="text-align:right">${t('bp.pdf.netProfit')}</th><th style="text-align:right">${t('bp.pdf.cumulative')}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <p style="margin-top:3rem;font-size:0.75rem;color:#9ca3af">${t('bp.pdf.footer')}</p>
  <button onclick="window.print()" style="margin-top:1rem;padding:12px 24px;background:#003a43;color:white;border:none;border-radius:8px;font-size:1rem;cursor:pointer">🖨️ ${t('bp.pdf.print')}</button>
</body></html>`;
  };

  const chartData = (bpData?.projections || [])
    .filter((_,i) => (i+1) % 6 === 0)
    .map(p => ({
      name: `M${p.month}`,
      [t('bp.chart.revenue')]: p.monthlyRevenue,
      [t('bp.chart.opex')]:    p.monthlyOpex,
      [t('bp.chart.cumulative')]: p.cumulativeProfit
    }));

  const steps = [t('bp.steps.opex'), t('bp.steps.capex'), t('bp.steps.projections'), t('bp.steps.myPlans')];

  return (
    <div className="page-content">
      <div style={{ marginBottom:'2rem' }}>
        <h1 style={{ fontFamily:'var(--font-head)', fontSize:'1.75rem', fontWeight:800, color:'var(--primary)' }}>
          {t('bp.title')}
        </h1>
        <p style={{ color:'var(--on-surface-muted)', marginTop:'0.25rem' }}>{t('bp.subtitle')}</p>
      </div>

      {/* Step indicator */}
      <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginBottom:'2.5rem' }}>
        {steps.map((s,i) => (
          <React.Fragment key={s}>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.375rem', cursor: i+1<=step?'pointer':'default' }} onClick={() => i+1<=step && setStep(i+1)}>
              <div style={{ width:40, height:40, borderRadius:'50%', background: i+1<=step?'var(--primary)':'var(--surface-high)', color: i+1<=step?'white':'var(--on-surface-muted)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.875rem' }}>
                {i+1}
              </div>
              <span style={{ fontSize:'0.7rem', fontWeight:600, color: i+1<=step?'var(--primary)':'var(--on-surface-muted)', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{s}</span>
            </div>
            {i<3 && <div style={{ flex:1, height:2, background: i+1<step?'var(--primary)':'var(--surface-high)', borderRadius:1 }} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="card" style={{ maxWidth:700 }}>
          <h3 style={{ fontFamily:'var(--font-head)', fontWeight:700, marginBottom:'1rem', color:'var(--primary)' }}>{t('bp.step2.title')}</h3>
          {opexList.length === 0
            ? <p style={{ color:'var(--on-surface-muted)' }}>{t('bp.step2.empty')} <a href="/opex">{t('bp.step2.emptyLink')}</a></p>
            : opexList.map(o => (
              <div key={o.id} onClick={() => {
                setSelectedOpex(o);
                setSelectedCapex(null);
              }}
                style={{ padding:'1.25rem', borderRadius:'var(--radius)', border:`2px solid ${selectedOpex?.id===o.id?'var(--primary)':'var(--surface-high)'}`, background: selectedOpex?.id===o.id?'rgba(0,58,67,0.04)':'white', cursor:'pointer', marginBottom:'0.75rem', display:'flex', justifyContent:'space-between', alignItems:'center', transition:'all 0.15s' }}>
                <div>
                  <div style={{ fontWeight:700 }}>{o.name || `Config ${o.id?.slice(0,8)}`}</div>
                  <div style={{ fontSize:'0.8rem', color:'var(--on-surface-muted)', marginTop:'0.25rem' }}>
                    {scenarioLabel(o)} | {t('bp.dailyCost')} {fmt(o.daily_cost)}
                    {o.config_id ? ` | ${t('bp.step2.quoteLinked')}` : ` | ${t('bp.step2.quoteUnlinked')}`}
                  </div>
                </div>
                {selectedOpex?.id===o.id && <div style={{ width:20, height:20, background:'var(--primary)', borderRadius:'50%' }} />}
              </div>
            ))
          }
          {selectedOpex && (
            <div style={{ marginTop:'1.5rem', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
              <div>
                <label className="label">{t('bp.step2.planName')}</label>
                <input className="input" placeholder={t('bp.step2.planNamePlaceholder')} value={planName} onChange={e => setPlanName(e.target.value)} />
              </div>
              <div>
                <label className="label">{t('bp.step2.workDays')}</label>
                <input className="input" type="number" value={workDays} onChange={e => setWorkDays(parseInt(e.target.value)||22)} />
              </div>

              {selectedOpexLinkedCapexId && (
                <>
                  <div style={{ gridColumn:'1 / -1', display:'flex', gap:'0.5rem' }}>
                    <button className={pricingMode === 'target_margin' ? 'btn-primary' : 'btn-secondary'} type="button" onClick={() => setPricingMode('target_margin')}>
                      Modalità commerciale
                    </button>
                    <button className={pricingMode === 'manual' ? 'btn-primary' : 'btn-secondary'} type="button" onClick={() => setPricingMode('manual')}>
                      Modalità libera (manuale)
                    </button>
                  </div>

                  {pricingMode === 'manual' && (
                    <>
                      <div>
                        <label className="label">{t('bp.step2.hourlyRate')}</label>
                        <input className="input" type="number" placeholder="0.00" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} />
                      </div>
                      <div style={{ display:'flex', alignItems:'flex-end' }}>
                        <div style={{ padding:'0.875rem 1rem', background:'var(--surface-low)', borderRadius:8, fontSize:'0.85rem', fontWeight:700, color:'var(--primary)' }}>
                          {fmt(parseFloat(hourlyRate || 0) * 8)}/{t('bp.step2.estimatedDay')}
                        </div>
                      </div>
                    </>
                  )}

                  {pricingMode === 'target_margin' && (
                    <>
                      <div>
                        <label className="label">Target margine</label>
                        <input className="input" type="number" placeholder="0.00" value={targetMarginValue} onChange={e => setTargetMarginValue(e.target.value)} />
                      </div>
                      <div>
                        <label className="label">Tipo margine</label>
                        <select className="input" value={targetMarginMode} onChange={e => setTargetMarginMode(e.target.value)}>
                          <option value="daily">€/giorno</option>
                          <option value="hourly">€/ora</option>
                        </select>
                      </div>
                      <div style={{ display:'flex', alignItems:'flex-end' }}>
                        <div style={{ padding:'0.875rem 1rem', background:'var(--surface-low)', borderRadius:8, fontSize:'0.82rem', fontWeight:700, color:'var(--primary)' }}>
                          Prezzo consigliato: {fmtH(targetPreview.hourly)}/h ({fmt(targetPreview.daily)}/giorno)
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {apiError && (
                <div style={{ gridColumn:'1 / -1', padding:'0.75rem 1rem', borderRadius:8, background:'#fee2e2', color:'#b91c1c', fontSize:'0.85rem', fontWeight:600 }}>
                  {apiError}
                </div>
              )}
            </div>
          )}
          {selectedOpex && (
            <div style={{ display:'flex', gap:'0.75rem', marginTop:'1.5rem' }}>
              {selectedOpexLinkedCapexId ? (
                <button className="btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={generate} disabled={loading || !selectedOpex || !planName || (pricingMode === 'manual' && !hourlyRate)}>
                  {loading ? t('bp.calculating') : `${t('bp.generate')} (CAPEX automatico)`} <ChevronRight size={16} />
                </button>
              ) : (
                <button className="btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={() => setStep(2)} disabled={!selectedOpex}>
                  {t('bp.next')} <ChevronRight size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="card" style={{ maxWidth:700 }}>
          <h3 style={{ fontFamily:'var(--font-head)', fontWeight:700, marginBottom:'1rem', color:'var(--primary)' }}>{t('bp.step1.title')}</h3>
          {capexList.length === 0
            ? <p style={{ color:'var(--on-surface-muted)' }}>{t('bp.step1.empty')} <a href="/capex">{t('bp.step1.emptyLink')}</a></p>
            : capexList.map(c => (
              <div key={c.id} onClick={() => setSelectedCapex(c)}
                style={{ padding:'1.25rem', borderRadius:'var(--radius)', border:`2px solid ${selectedCapex?.id===c.id?'var(--primary)':'var(--surface-high)'}`, background: selectedCapex?.id===c.id?'rgba(0,58,67,0.04)':'white', cursor:'pointer', marginBottom:'0.75rem', display:'flex', justifyContent:'space-between', alignItems:'center', transition:'all 0.15s' }}>
                <div>
                  <div style={{ fontWeight:700 }}>{c.name}</div>
                  <div style={{ fontSize:'0.8rem', color:'var(--on-surface-muted)', marginTop:'0.25rem' }}>
                    {c.drone_model} × {c.num_drones} {t('common.drones').toLowerCase()} | CAPEX {fmt(c.total_capex)}
                  </div>
                </div>
                {selectedCapex?.id===c.id && <div style={{ width:20, height:20, background:'var(--primary)', borderRadius:'50%' }} />}
              </div>
            ))
          }
          {!!selectedOpex && !selectedOpexLinkedCapexId && (
            <div style={{ marginTop:'1.5rem', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
              <div style={{ gridColumn:'1 / -1', display:'flex', gap:'0.5rem' }}>
                <button className={pricingMode === 'target_margin' ? 'btn-primary' : 'btn-secondary'} type="button" onClick={() => setPricingMode('target_margin')}>
                  Modalità commerciale
                </button>
                <button className={pricingMode === 'manual' ? 'btn-primary' : 'btn-secondary'} type="button" onClick={() => setPricingMode('manual')}>
                  Modalità libera (manuale)
                </button>
              </div>

              {pricingMode === 'manual' && (
                <>
                  <div>
                    <label className="label">{t('bp.step2.hourlyRate')}</label>
                    <input className="input" type="number" placeholder="0.00" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} />
                  </div>
                  <div style={{ display:'flex', alignItems:'flex-end' }}>
                    <div style={{ padding:'0.875rem 1rem', background:'var(--surface-low)', borderRadius:8, fontSize:'0.85rem', fontWeight:700, color:'var(--primary)' }}>
                      {fmt(parseFloat(hourlyRate || 0) * 8)}/{t('bp.step2.estimatedDay')}
                    </div>
                  </div>
                </>
              )}

              {pricingMode === 'target_margin' && (
                <>
                  <div>
                    <label className="label">Target margine</label>
                    <input className="input" type="number" placeholder="0.00" value={targetMarginValue} onChange={e => setTargetMarginValue(e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Tipo margine</label>
                    <select className="input" value={targetMarginMode} onChange={e => setTargetMarginMode(e.target.value)}>
                      <option value="daily">€/giorno</option>
                      <option value="hourly">€/ora</option>
                    </select>
                  </div>
                  <div style={{ display:'flex', alignItems:'flex-end' }}>
                    <div style={{ padding:'0.875rem 1rem', background:'var(--surface-low)', borderRadius:8, fontSize:'0.82rem', fontWeight:700, color:'var(--primary)' }}>
                      Prezzo consigliato: {fmtH(targetPreview.hourly)}/h ({fmt(targetPreview.daily)}/giorno)
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {apiError && (
            <div style={{ marginTop:'1rem', padding:'0.75rem 1rem', borderRadius:8, background:'#fee2e2', color:'#b91c1c', fontSize:'0.85rem', fontWeight:600 }}>
              {apiError}
            </div>
          )}
          <div style={{ display:'flex', gap:'0.75rem', marginTop:'1.5rem' }}>
            <button className="btn-secondary" style={{ flex:1, justifyContent:'center' }} onClick={() => setStep(1)}>{t('bp.back')}</button>
            <button className="btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={generate} disabled={loading || !selectedOpex || !selectedCapex || !planName || (pricingMode === 'manual' && !hourlyRate)}>
              {loading ? t('bp.calculating') : t('bp.generate')} <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && bpData && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:'2rem' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem' }}>
              {[
                [t('bp.kpi.capex'),    fmt(bpData.totalCapex),     'var(--primary)'],
                [t('bp.kpi.opex'),     fmt(bpData.monthlyOpex),    'var(--teal)'],
                [t('bp.kpi.revenue'),  fmt(bpData.monthlyRevenue), '#10b981'],
                [t('bp.kpi.profit'),   fmt(bpData.monthlyProfit),  bpData.monthlyProfit>=0?'#10b981':'#ef4444']
              ].map(([l,v,c]) => (
                <div key={l} className="card" style={{ padding:'1rem' }}>
                  <div style={{ fontSize:'0.7rem', color:'var(--on-surface-muted)', textTransform:'uppercase', marginBottom:'0.375rem' }}>{l}</div>
                  <div style={{ fontFamily:'var(--font-head)', fontSize:'1.2rem', fontWeight:800, color:c }}>{v}</div>
                </div>
              ))}
            </div>

            <div className="card">
              <h3 style={{ fontFamily:'var(--font-head)', fontWeight:700, marginBottom:'0.25rem', color:'var(--primary)' }}>{t('bp.chart.revenueVsOpex')}</h3>
              <p style={{ color:'var(--on-surface-muted)', fontSize:'0.8rem', marginBottom:'1.5rem' }}>{t('bp.chart.every6months')}</p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData} barSize={24} barGap={4}>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize:12, fill:'#6b7280' }} />
                  <YAxis hide />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Bar dataKey={t('bp.chart.revenue')} fill="#10b981" radius={[4,4,0,0]} />
                  <Bar dataKey={t('bp.chart.opex')}    fill="var(--teal)" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 style={{ fontFamily:'var(--font-head)', fontWeight:700, marginBottom:'0.25rem', color:'var(--primary)' }}>{t('bp.chart.roiTitle')}</h3>
              <p style={{ color:'var(--on-surface-muted)', fontSize:'0.8rem', marginBottom:'1.5rem' }}>{t('bp.chart.roiDesc')}</p>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize:12, fill:'#6b7280' }} />
                  <YAxis tickFormatter={(v) => fmt(v)} tick={{ fontSize:11, fill:'#6b7280' }} width={90} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <ReferenceLine y={0} stroke="#10b981" strokeDasharray="4 4" label={{ value:'Break-Even', fill:'#10b981', fontSize:11, fontWeight:700 }} />
                  <Line dataKey={t('bp.chart.cumulative')} stroke="var(--primary)" strokeWidth={2.5} dot={{ r:4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="card" style={{ overflowX:'auto' }}>
              <h3 style={{ fontFamily:'var(--font-head)', fontWeight:700, marginBottom:'1rem', color:'var(--primary)' }}>{t('bp.table.title')}</h3>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid var(--surface-high)' }}>
                    {[t('bp.table.month'), t('bp.table.revenue'), t('bp.table.opex'), t('bp.table.netProfit'), t('bp.table.cumulative')].map(h => (
                      <th key={h} style={{ padding:'0.75rem', textAlign: h===t('bp.table.month')?'left':'right', fontWeight:700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(bpData.projections || []).filter((_,i) => (i+1)%6===0).map(p => (
                    <tr key={p.month} style={{ borderBottom:'1px solid var(--surface-high)' }}>
                      <td style={{ padding:'0.75rem' }}>{t('bp.table.month')} {p.month}</td>
                      <td style={{ padding:'0.75rem', textAlign:'right', color:'#10b981', fontWeight:600 }}>{fmt(p.monthlyRevenue)}</td>
                      <td style={{ padding:'0.75rem', textAlign:'right', color:'var(--teal)', fontWeight:600 }}>{fmt(p.monthlyOpex)}</td>
                      <td style={{ padding:'0.75rem', textAlign:'right', fontWeight:600 }}>{fmt(p.monthlyProfit)}</td>
                      <td style={{ padding:'0.75rem', textAlign:'right', fontWeight:700, color: p.cumulativeProfit>=0?'#10b981':'#ef4444' }}>{fmt(p.cumulativeProfit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:'1rem', position:'sticky', top:'1.5rem', alignSelf:'flex-start' }}>
            <div className="teal-card">
              <div style={{ fontSize:'0.7rem', opacity:0.7, textTransform:'uppercase', letterSpacing:'0.1em' }}>{t('bp.sidebar.breakeven')}</div>
              <div style={{ display:'flex', alignItems:'baseline', gap:'0.5rem', margin:'0.5rem 0' }}>
                <span style={{ fontFamily:'var(--font-head)', fontSize:'2.5rem', fontWeight:800 }}>{bpData.breakevenMonth ?? '—'}</span>
                <span style={{ fontSize:'1rem', opacity:0.8 }}>{bpData.breakevenMonth ? t('common.months') : 'N/A'}</span>
              </div>
              <div style={{ fontSize:'0.8rem', opacity:0.7 }}>
                {bpData.breakevenMonth ? t('bp.sidebar.breakevenDesc', { month: bpData.breakevenMonth }) : t('bp.sidebar.breakevenNA')}
              </div>
            </div>
            <div className="stat-card">
              <div style={{ fontSize:'0.7rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>{t('bp.sidebar.roi60')}</div>
              <div style={{ fontFamily:'var(--font-head)', fontSize:'1.75rem', fontWeight:800, color:'var(--primary)', margin:'0.375rem 0' }}>{bpData.roi60}%</div>
              <div style={{ display:'flex', alignItems:'center', gap:'0.375rem', color:'#10b981', fontSize:'0.8rem', fontWeight:600 }}>
                <TrendingUp size={14} /> {t('bp.sidebar.cumProfit')} {fmt(bpData.projections?.[59]?.cumulativeProfit)}
              </div>
            </div>
            <button className="btn-primary" style={{ justifyContent:'center', gap:'0.5rem', padding:'1rem' }} onClick={downloadPDF}>
              <Download size={16} /> {t('bp.downloadPDF')}
            </button>
            <button className="btn-secondary" style={{ justifyContent:'center' }} onClick={() => setStep(4)}>
              {t('bp.viewAll')}
            </button>
          </div>
        </div>
      )}

      {/* STEP 4 */}
      {step === 4 && (
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
            <h3 style={{ fontFamily:'var(--font-head)', fontWeight:700, color:'var(--primary)' }}>{t('bp.myPlans.title')}</h3>
            <button className="btn-primary" style={{ gap:'0.5rem' }} onClick={() => {
              setBpData(null);
              setSelectedCapex(null);
              setSelectedOpex(null);
              setHourlyRate('');
              setPricingMode('target_margin');
              setTargetMarginMode('daily');
              setTargetMarginValue('150');
              setApiError('');
              setPlanName('');
              setStep(1);
            }}>
              <Plus size={16} /> {t('bp.myPlans.new')}
            </button>
          </div>
          {bpList.length === 0
            ? <p style={{ color:'var(--on-surface-muted)', textAlign:'center', padding:'2rem' }}>{t('bp.myPlans.empty')}</p>
            : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid var(--surface-high)', fontSize:'0.8rem', fontWeight:700 }}>
                    {[t('common.name'), t('bp.myPlans.drone'), 'CAPEX', t('bp.myPlans.opexType'), t('bp.myPlans.priceH'), t('common.date'), ''].map(h => (
                      <th key={h} style={{ padding:'0.75rem', textAlign: h===''?'right':'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bpList.map(bp => (
                    <tr key={bp.id} style={{ borderBottom:'1px solid var(--surface-high)', fontSize:'0.85rem' }}>
                      <td style={{ padding:'0.75rem', fontWeight:600 }}>{bp.name}</td>
                      <td style={{ padding:'0.75rem' }}>{bp.drone_model} ×{bp.num_drones}</td>
                      <td style={{ padding:'0.75rem' }}>{fmt(bp.total_capex)}</td>
                      <td style={{ padding:'0.75rem' }}>{bp.scenario_type === 'middle_mile' ? '🚁 Middle Mile' : '☀️ Solar Panel'}</td>
                      <td style={{ padding:'0.75rem', fontWeight:700, color:'var(--primary)' }}>{fmtH(bp.service_hourly_rate)}/h</td>
                      <td style={{ padding:'0.75rem', color:'var(--on-surface-muted)' }}>{new Date(bp.created_at).toLocaleDateString('it-IT')}</td>
                      <td style={{ padding:'0.75rem', textAlign:'right', display:'flex', gap:'0.5rem', justifyContent:'flex-end' }}>
                        <button onClick={() => loadBPById(bp.id)} style={{ padding:'0.5rem', background:'#e0e7ff', border:'none', borderRadius:6, cursor:'pointer', display:'inline-flex', alignItems:'center' }} title={t('common.open')}>
                          <ChevronRight size={14} color="#3730a3" />
                        </button>
                        <button onClick={() => handleDelete(bp.id)} style={{ padding:'0.5rem', background:'#fee2e2', border:'none', borderRadius:6, cursor:'pointer', display:'inline-flex', alignItems:'center' }} title={t('common.delete')}>
                          <Trash2 size={14} color="#ef4444" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}
    </div>
  );
}
